import React, { useState } from 'react';
import { Input } from '../../../components/common/Form';

export interface ContactData {
  firstName: string;
  lastName: string;
  title: string;
  phone: string;
  email: string;
}

interface LocationContactStepProps {
  data: ContactData;
  onChange: (data: ContactData) => void;
  onSubmit: () => void;
  onBack: () => void;
}

type ContactField = keyof ContactData;
type ContactErrors = Partial<Record<ContactField, string>>;

const INPUT_OVERRIDE = `
  .location-wizard-form input {
    padding: 10px 12px !important;
    font-size: 0.8125rem !important;
    height: 40px !important;
    box-sizing: border-box !important;
  }
`;

const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const validateContact = (data: ContactData): ContactErrors => {
  const errors: ContactErrors = {};

  if (!data.firstName.trim()) {
    errors.firstName = 'First name is required.';
  }

  if (!data.lastName.trim()) {
    errors.lastName = 'Last name is required.';
  }

  if (!data.title.trim()) {
    errors.title = 'Title is required.';
  }

  if (!data.email.trim()) {
    errors.email = 'Email address is required.';
  } else if (!isValidEmail(data.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }

  return errors;
};

const LocationContactStep = ({ data, onChange, onSubmit, onBack }: LocationContactStepProps) => {
  const [errors, setErrors] = useState<ContactErrors>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const updateField = (field: ContactField, value: string) => {
    const updatedData = { ...data, [field]: value };
    onChange(updatedData);

    if (hasAttemptedSubmit) {
      setErrors(validateContact(updatedData));
    }
  };

  const handleSubmit = () => {
    setHasAttemptedSubmit(true);

    const nextErrors = validateContact(data);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length === 0) {
      onSubmit();
    }
  };

  return (
    <div className="location-wizard-form" style={{ width: '100%' }}>
      <style>{INPUT_OVERRIDE}</style>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '32px'
        }}
      >
        <div>
          <Input
            name="firstName"
            label="FIRST NAME"
            value={data.firstName}
            error={errors.firstName}
            touched={hasAttemptedSubmit}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('firstName', e.target.value)}
          />
        </div>

        <div>
          <Input
            name="lastName"
            label="LAST NAME"
            value={data.lastName}
            error={errors.lastName}
            touched={hasAttemptedSubmit}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('lastName', e.target.value)}
          />
        </div>
      </div>

      <div style={{ marginTop: '8px' }}>
        <Input
          name="title"
          label="TITLE"
          value={data.title}
          error={errors.title}
          touched={hasAttemptedSubmit}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('title', e.target.value)}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '32px',
          marginTop: '8px'
        }}
      >
        <Input
          name="phone"
          label="PHONE NUMBER"
          value={data.phone}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('phone', e.target.value)}
        />

        <div>
          <Input
            name="email"
            label="EMAIL ADDRESS"
            value={data.email}
            error={errors.email}
            touched={hasAttemptedSubmit}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('email', e.target.value)}
          />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '8px'
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            width: '167px',
            height: '35px',
            backgroundColor: 'var(--color-disabled)',
            color: 'white',
            borderRadius: '999px',
            border: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '1.4px',
            cursor: 'pointer'
          }}
        >
          BACK
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          style={{
            width: '167px',
            height: '35px',
            backgroundColor: 'var(--color-accent)',
            color: 'white',
            borderRadius: '999px',
            border: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '1.4px',
            cursor: 'pointer'
          }}
        >
          SUBMIT
        </button>
      </div>
    </div>
  );
};

export default LocationContactStep;
